import {
	List,
	mergeStyles,
	PrimaryButton,
	Stack,
	useTheme,
} from '@fluentui/react';
import {LoadWrapper} from 'components/LoadWrapper';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
	useDeferredValue,
} from 'react';
import {useTranslation} from 'react-i18next';
import {getListClassNames} from 'styles';
import {KeywordStatus, VexWorkbenchItemSimplified} from 'types';
import {RegulationHeaderRow, WorkbenchHeader} from '../Headers';
import {useDashboardQueries} from 'features/Dashboard/hooks/useDashboardQueries';
import {useUserContext} from 'authentication/UserContext';
import {FilterItem} from 'components';
import {useVexWorkbenchItemsFilter} from './useVexWorkbenchItemsFilter';
import {VexWorkbenchItemCell} from './VexWorkbenchItemCell';

export type DashboardCardProps = {
	halfHeight?: boolean;
	itemCountLoaded?: (count: number) => void;
	isVkoApproverDashboard?: boolean;
};

type VexWorkbenchItemsProps = {
	keywordStatus:
		| KeywordStatus.Finalized
		| KeywordStatus.Accepted
		| KeywordStatus.New;
} & DashboardCardProps;

export const VexWorkbenchItems: React.FC<VexWorkbenchItemsProps> = ({
	halfHeight = false,
	keywordStatus,
	itemCountLoaded,
}) => {
	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'VexWorkbenchItems',
	});
	const getTitle = useCallback(
		(keywordStatus: any) => {
			switch (keywordStatus) {
				case KeywordStatus.Accepted:
					return t('InProcessTitle');
				case KeywordStatus.Finalized:
					return t('CompletedTitle');
				case KeywordStatus.New:
					return t('AssessResponsibilityTitle');
				default:
					return t('DefaultTitle');
			}
		},
		[t],
	);
	const proportionTable = useMemo(
		() =>
			getTitle(keywordStatus) === t('CompletedTitle')
				? [60, 20, 20]
				: [40, 20, 20, 20],
		[getTitle, keywordStatus, t],
	);

	const {getVexWorkbenchItemsQuery} = useDashboardQueries();
	const {myVexClusters} = useUserContext();
	const {data, loading} = getVexWorkbenchItemsQuery(keywordStatus);
	const [filterPanel, setFilterPanel] = useState(false);

	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);

	const items: VexWorkbenchItemSimplified[] = useMemo(
		() => data?.vexWorkbenchItems || [],
		[data],
	);

	// Defer items to prevent blocking UI during large updates
	const deferredItems = useDeferredValue(items);

	const {
		filteredItems,
		filters,
		setFilters,
		filterLegalAreas,
		filterStatuses,
		filterVexClusters,
	} = useVexWorkbenchItemsFilter(deferredItems);

	useEffect(() => {
		if (!loading && data?.vexWorkbenchItems) {
			itemCountLoaded?.(data.vexWorkbenchItems?.length);
		}
	}, [data, loading, itemCountLoaded]);

	const renderCell = useCallback(
		(item?: VexWorkbenchItemSimplified) => {
			if (!item) {
				return null;
			}
			return (
				<VexWorkbenchItemCell
					keywordStatus={keywordStatus}
					item={item as any}
					columnKey={getTitle(keywordStatus)}
					proportionTable={proportionTable}
					halfHeight={halfHeight}
				/>
			);
		},
		[keywordStatus, getTitle, proportionTable, halfHeight],
	);
	const setSelectedFilters = (filters: any) => {
		setFilters(filters);
	};

	const filterContainerStyle = useMemo(
		() => ({
			height: 310,
			bottom: 10,
			width: '100%',
			justifyContent: 'flex-end',
		}),
		[],
	);

	const buttonStackStyle = useMemo(
		() => ({
			height: 50,
			bottom: 10,
			width: '100%',
			justifyContent: 'flex-end' as const,
		}),
		[],
	);

	const filterStackStyle = useMemo(
		() => ({justifyContent: 'space-between' as const, height: '100%'}),
		[],
	);

	const filterPanelContent = useMemo(
		() => (
			<div
				className={mergeStyles(
					classNames.filterPanelContainer,
					classNames.container,
				)}
				data-is-scrollable
			>
				<div style={filterContainerStyle}>
					<Stack horizontal tokens={{childrenGap: 10}} style={buttonStackStyle}>
						<PrimaryButton
							text={t('ClearFilters')}
							onClick={() => {
								setFilters([]);
								setFilterPanel(false);
							}}
						/>
						<PrimaryButton
							text={t('ApplyFilters')}
							onClick={() => setFilterPanel(false)}
						/>
					</Stack>
					<Stack
						horizontal
						style={{justifyContent: 'space-between', height: '100%'}}
					>
						<Stack.Item>
							<FilterItem
								col={{key: 'LegalArea', name: t('LegalArea'), minWidth: 10}}
								blanksExists={false}
								selectedFilters={filters}
								setSelectedFilters={setSelectedFilters}
								getFilterFn={(match: any) => (item: any) => true}
								getBlankFilterFn={(item: any) => true}
								sortedValues={filterLegalAreas}
								expandedByDefault={true}
							/>
						</Stack.Item>
						<Stack.Item>
							<FilterItem
								col={{key: 'Status', name: t('Status'), minWidth: 10}}
								blanksExists={false}
								selectedFilters={filters}
								setSelectedFilters={setSelectedFilters}
								getFilterFn={(match: any) => (item: any) => true}
								getBlankFilterFn={(item: any) => true}
								sortedValues={filterStatuses}
								expandedByDefault={true}
							/>
						</Stack.Item>
						<Stack.Item>
							<FilterItem
								col={{key: 'VexCluster', name: t('VexCluster'), minWidth: 10}}
								blanksExists={false}
								selectedFilters={filters}
								setSelectedFilters={setSelectedFilters}
								getFilterFn={(match: any) => (item: any) => true}
								getBlankFilterFn={(item: any) => true}
								sortedValues={filterVexClusters}
								expandedByDefault={true}
							/>
						</Stack.Item>
					</Stack>
				</div>
			</div>
		),
		[
			classNames,
			filters,
			filterLegalAreas,
			filterStatuses,
			filterVexClusters,
			t,
			setFilters,
			setSelectedFilters,
			filterContainerStyle,
			buttonStackStyle,
			filterStackStyle,
		],
	);

	return (
		<Stack.Item grow={1} basis={0}>
			<div className={classNames.dashboardActivityContainer}>
				<div>
					<WorkbenchHeader
						halfHeight
						title={getTitle(keywordStatus)}
						items={
							keywordStatus === KeywordStatus.Finalized
								? undefined
								: items.length
						}
						filterIconCallback={() => setFilterPanel(!filterPanel)}
						anyFilterSet={filters.length > 0}
					/>
					{filterPanel ? (
						filterPanelContent
					) : (
						<div className={classNames.container} data-is-scrollable>
							<RegulationHeaderRow
								proportionTable={proportionTable}
								columnKey={getTitle(keywordStatus)}
								t={t}
							/>
							<LoadWrapper loading={loading}>
								<List items={filteredItems} onRenderCell={renderCell} />
							</LoadWrapper>
						</div>
					)}
				</div>
			</div>
		</Stack.Item>
	);
};
